/*
 * Models for substitution groups
 */

define([
  "backbone",
  "messaging/messenger",
  "configuration/properties",
  "jquery",
  "modules/shared/sortableList/sortableListModel",
  "modules/shared/history/historyModel",
], function (Backbone, Messenger, Properties, $, SortableListModel, HistoryModel) {
  var SubstitutionModel = {};

  SubstitutionModel.substitutionGroupEntry = Backbone.Model.extend({
    defaults: {
      entrySurfaceForm: "",
      defaultEntry: false,
    },
    _validateDuplicateEntrySurfaceForm: function (value) {
      for (let i = 0; i < this.collection.length; i++) {
        const entry = this.collection.at(i);
        if (this !== entry) {
          //don't check the entry against itself
          if (value.toUpperCase() === entry.get("entrySurfaceForm").toUpperCase()) {
            return $.t("substitutions.entries.validationError");
          }
        }
      }
    },
    validateAttribute: function (attribute, value) {
      if (attribute === "entrySurfaceForm") {
        return (
          this.validateRequiredValue(value, { maxLength: 1000 }) ||
          this._validateDuplicateEntrySurfaceForm(value)
        );
      }
    },

    idAttribute: "entryId",
  });

  SubstitutionModel.substitutionGroupEntries = Backbone.Collection.extend({
    model: SubstitutionModel.substitutionGroupEntry,
    comparator: function (a, b) {
      return Backbone.textComparator(a, b, "entrySurfaceForm");
    },
    addNewEntry: function () {
      var newEntry = new SubstitutionModel.substitutionGroupEntry();
      this.add(newEntry);
      return newEntry; //return new entry so we can easily manipulate it without searching
    },
  });

  SubstitutionModel.substitutionGroup = Backbone.Model.extend({
    defaults: {
      name: "",
      description: "",
    },
    entries: function () {
      this.entriesCol = this.getSubModel(
        this.entriesCol,
        SubstitutionModel.substitutionGroupEntries,
        "/entries",
        true,
      );
      return this.entriesCol;
    },
    idAttribute: "substId",
    saveChildren: function (options) {
      this.url = this.get("self").href; //set group url
      this.entriesCol.saveCollection(options);
    },
    _createSaveModelSuccessWithChildren: function (options) {
      return (model) => {
        model.dirty = false;
        model.entriesCol.url = model.get("entries").href;
        model.entriesCol.saveCollection();
      };
    },
    _validateDuplicateSubstitutionGroup: function (value) {
      if (this.collection) {
        for (let i = 0; i < this.collection.length; i++) {
          const group = this.collection.at(i);
          if (this !== group) {
            //don't check the group against itself
            if (value.toUpperCase() === group.get("name").toUpperCase()) {
              return $.t("substitutions.group.validationError");
            }
          }
        }
      }
    },
    validateAttribute: function (attribute, value) {
      if (attribute === "name") {
        return (
          this.validateRequiredValue(value, { maxLength: 128 }) ||
          this.validateSafeCharacters(value) ||
          this._validateDuplicateSubstitutionGroup(value)
        );
      }
      if (attribute === "description") {
        return this.validateStringLength(value, { maxLength: 1000 });
      }
    },
    validate: function () {
      const error = {};
      let isValid = true;
      //validate name field
      let message = this.validateAttribute("name", this.get("name"));
      if (message) {
        error["name"] = message;
        isValid = false;
      }
      message = this.validateAttribute("description", this.get("description"));
      if (message) {
        error["description"] = message;
        isValid = false;
      }
      //validate that group has at least one entry
      if (this.entries().size() === 0) {
        error["entries"] = $.t("substitutions.group.noEntriesError");
        isValid = false;
      }
      if (!isValid) {
        return error;
      }
    },
    history: function () {
      var history = new HistoryModel.historyCollection();
      if (!this.isNew()) {
        var url = this.get("self").href + "/history";
        history.url = function () {
          return url;
        };
        history.fetch();
      }
      return history;
    },
  });

  SubstitutionModel.substitutionGroups = Backbone.Collection.extend({
    model: SubstitutionModel.substitutionGroup,
    url: function () {
      var kbId = Messenger.reqres.request("active:kb");
      return Properties.rootURL + "/" + kbId + "/substitutions";
    },
    comparator: function (a, b) {
      return Backbone.textComparator(a, b, "name");
    },
    search: function (query) {
      //search collection by name
      return new SubstitutionModel.substitutionGroups(
        this.filter(function (group) {
          //return a new Collection
          var pattern = RegExp(".*" + query + ".*", "gi"); //case insensitive partial matching
          return pattern.test(group.get("name"));
        }),
      );
    },
    addNewGroup: function () {
      var group = new SubstitutionModel.substitutionGroup();
      this.add(group);
      return group;
    },
  });

  //handler to load Substitution groups
  Messenger.reqres.setHandler("substitutions", function () {
    console.log("get subs");
    var subs = new SubstitutionModel.substitutionGroups();
    subs.on("sync", function () {
      this.sort();
    });
    subs.fetch();
    return subs;
  });

  //handler to load substitution groups by their entry names (we can filter by group name locally)
  Messenger.reqres.setHandler("filtered:substitutions", function (query) {
    var subs = new SubstitutionModel.substitutionGroups();
    subs.on("sync", function () {
      this.sort();
    });
    var oldURL = subs.url;
    //change fetch url to be a request for groups by entry name
    subs.url = function () {
      var kbId = Messenger.reqres.request("active:kb");
      return (
        `${Properties.rootURL}/${kbId}/` +
        `substitutions/entry_name/search` +
        `?entryName=${encodeURIComponent(query)}`
      );
    };
    subs.fetch();
    //set url back to orginal value
    subs.url = oldURL;
    return subs;
  });

  //Request for the headers of the substitution history list
  Messenger.reqres.setHandler("substitution:history:headers", function () {
    var Header = SortableListModel.headerModel;
    return new Backbone.Collection([
      new Header({
        headerName: $.t("history.user"),
        modelAttribute: "userName",
        sortable: true,
        filterable: true,
        textField: true,
      }),
      new Header({
        headerName: $.t("history.date"),
        modelAttribute: "dateTime",
        sortable: true,
        defaultSort: true,
        defaultReversed: true,
        dateField: true,
      }),
      new Header({
        headerName: $.t("history.change"),
        modelAttribute: "changeType",
        sortable: true,
        filterable: true,
        textField: true,
      }),
      new Header({
        headerName: $.t("history.oldValue"),
        modelAttribute: "oldValue",
        sortable: true,
        textField: true,
      }),
      new Header({
        headerName: $.t("history.newValue"),
        modelAttribute: "newValue",
        sortable: true,
        textField: true,
      }),
    ]);
  });

  Messenger.reqres.setHandler("search:substitution:id", function (id) {
    var response = new SubstitutionModel.substitutionGroup();
    response.url = function () {
      var kbId = Messenger.reqres.request("active:kb");
      return Properties.rootURL + "/" + kbId + "/substitutions/" + id;
    };
    response.fetch();
    return response;
  });

  Messenger.reqres.setHandler("search:substitution:name", function (name) {
    var response = new SubstitutionModel.substitutionGroups();
    response.url = function () {
      var kbId = Messenger.reqres.request("active:kb");
      return (
        `${Properties.rootURL}/${kbId}/` +
        `substitutions/group_name/exact/search` +
        `?substName=${encodeURIComponent(name)}`
      );
    };
    response.fetch();
    return response;
  });
});
